import api, { type AxiosError } from '@mint-lib/api';

import { objectKeysToSnakeCase } from '@myn/shared/src/utils';

import {
  mdmFieldCodec,
  normalizeSearchParams,
  parseApiError,
} from '../utils/index.js';
import type {
  CellFormatType,
  DataFileContext,
  DataFileFormats,
  DataFilePreview,
  GetAllPlatformsListResponse,
  GetCampaignsListResponse,
  GetPlatformNamesListResponse,
  GranularityLevel,
  NewImportTemplatePayload,
  S3UploadCredentials,
  TableDataFileTotalErrorsCountResponse,
  TableDataFileValidatedRowsResponse,
  TemplateCollaborator,
  TotalFilesToArchiveCountResponse,
} from './types.js';

api.interceptors.response.use(undefined, (err: AxiosError) => {
  throw parseApiError(err);
});

export const getCampaignsList = async () => {
  const searchParams = normalizeSearchParams({
    withDates: true,
    excludeDeletedPlatform: true,
  });

  return api
    .get<GetCampaignsListResponse>(
      '/api/datintell/v1/builder/campaign_collections/preview/',
      { params: searchParams },
    )
    .then((res) => res.data);
};

export const getPlatformsByCampaign = async (campaignCollectionId: number) => {
  const searchParams = normalizeSearchParams({
    campaignCollectionId,
    withDates: true,
    excludeDeletedPlatform: true,
  });

  return api
    .get<GetPlatformNamesListResponse>(
      `/api/datintell/v1/builder/campaign_platform_names/preview/`,
      { params: searchParams },
    )
    .then((res) => res.data);
};

export const getAllPlatformsList = async (params: {
  campaignCollectionId?: null | number;
  channelName?: null | string;
}): Promise<GetAllPlatformsListResponse> => {
  const searchParams = normalizeSearchParams({
    ...params,
    withDates: true,
    excludeDeletedPlatform: true,
  });

  return api
    .get(`/api/datintell/v1/builder/all_platform_names/preview/`, {
      params: searchParams,
    })
    .then((res) => res.data);
};

export const getChannelNamesList = async (params: {
  campaignCollectionId?: null | number;
  platformName?: null | string;
  tileId?: null | string;
}): Promise<string[]> => {
  const searchParams = normalizeSearchParams({ ...params, withDates: true });

  return api
    .get(`/api/datintell/v1/builder/channel_names/preview/`, {
      params: searchParams,
    })
    .then((res) => res.data.names);
};

export const getExampleCsvFile = () => {
  return api
    .get<BlobPart>(`/api/data-ingestion/file-example/`)
    .then((res) => res.data);
};

export const getTotalFilesToArchiveCount = () => {
  return api
    .get<TotalFilesToArchiveCountResponse>(
      `/api/data-ingestion/tabledatafile/files-to-archive-count/`,
    )
    .then((res) => res.data.data.items[0].totalFilesToArchive);
};

export const deleteTableDataFile = (id: string) => {
  return api
    .delete(`/api/data-ingestion/tabledatafile/${id}/`)
    .then((res) => res.data.data);
};

export const createTemplate = (payload: NewImportTemplatePayload) => {
  return api
    .post('/api/data-ingestion/templates/', payload)
    .then((res) => res.data);
};

export const deleteTemplate = (id: string): Promise<unknown> => {
  return api.delete(`/api/data-ingestion/templates/${id}/`);
};

export const goToNextStep = (id: string): Promise<unknown> => {
  return api.post(`/api/data-ingestion/tabledatafile/${id}/next-step/`);
};

export const goToPrevStep = (id: string): Promise<unknown> => {
  return api.post(`/api/data-ingestion/tabledatafile/${id}/back-step/`);
};

export const updateTableDataFileContext = async ({
  id,
  payload,
}: {
  id: string;
  payload: DataFileContext;
}): Promise<DataFilePreview> => {
  return api
    .post<DataFilePreview>(`/api/data-ingestion/tabledatafile/${id}/context/`, {
      context: payload,
    })
    .then((res) => res.data);
};

export const updateTableDataFileHeader = ({
  payload,
}: {
  payload: { rowId: string };
}): Promise<unknown> => {
  return api.post('/api/data-ingestion/rows/select-header/', payload);
};

export const getS3UploadCredentials = (
  id: string,
): Promise<S3UploadCredentials> => {
  return api
    .get(`/api/data-ingestion/tabledatafile/${id}/post-signed-url/`)
    .then((res) => res.data.data.items[0]);
};

export const uploadFileToS3 = async ({
  awsUrl,
  payload,
}: {
  awsUrl: string;
  payload: FormData;
}): Promise<void> => {
  return api.post(awsUrl, payload, {
    transformRequest: [
      (data, headers) => {
        delete headers.Authorization;
        return data;
      },
    ],
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const startParsingFile = ({
  dataFileId,
  payload,
}: {
  dataFileId: string;
  payload: { fileName: string; resetFile?: boolean };
}): Promise<void> => {
  return api.post(
    `/api/data-ingestion/tabledatafile/${dataFileId}/start-parsing-file/`,
    payload,
  );
};

export const updateHeader = ({
  id,
  payload,
}: {
  id: string;
  payload: { name: string | null; column_position: number };
}): Promise<unknown> => {
  return api.post(
    `/api/data-ingestion/tabledatafile/${id}/set-header/`,
    payload,
  );
};

export const discardHeader = ({
  id,
  payload,
}: {
  id: string;
  payload: { name: string | null; column_position: number };
}): Promise<unknown> => {
  return api.post(
    `/api/data-ingestion/tabledatafile/${id}/discard-header/`,
    payload,
  );
};

export const updateFormats = ({
  id,
  payload,
}: {
  id: string;
  payload: DataFileFormats;
}): Promise<unknown> => {
  return api.post(`/api/data-ingestion/tabledatafile/${id}/formats/`, payload);
};

export const getTableDataFileTotalErrorsCount = (id: string) => {
  return api
    .get<TableDataFileTotalErrorsCountResponse>(
      `/api/data-ingestion/tabledatafile/${id}/validated-rows/total_errors_count/`,
    )
    .then((res) => res.data.data.items[0]);
};

export const getTableDataFileValidatedRowsSummary = (id: string) => {
  return api
    .get<TableDataFileTotalErrorsCountResponse>(
      `/api/data-ingestion/tabledatafile/${id}/validated-rows/summary/`,
    )
    .then((res) => res.data.data.items[0]);
};

export const getTableDataFileValidatedRows = (
  id: string,
  params: {
    pageNumber?: number;
    itemsPerPage?: number;
    onlyErrors?: boolean;
  } = {},
) => {
  const urlSearchParams = new URLSearchParams(
    Object.entries(objectKeysToSnakeCase(params)).map(([key, value]) => [
      key,
      String(value),
    ]),
  );
  const queryString = urlSearchParams.toString();

  return api
    .get<{
      data: TableDataFileValidatedRowsResponse;
    }>(`/api/data-ingestion/tabledatafile/${id}/validated-rows/?${queryString}`)
    .then((res) => {
      const data = res.data.data;

      data.additional.headers = data.additional.headers.map((header) => ({
        ...header,
        headerName: mdmFieldCodec.decode(header.headerName),
        formatType: mdmFieldCodec.decode(header.formatType) as CellFormatType,
      }));

      return data;
    });
};

export const discardTableDataFileRows = ({
  id,
  payload,
}: {
  id: string;
  payload:
    | { includeIds: string[] }
    | { excludeIds: string[]; onlyErrors?: boolean };
}): Promise<void> => {
  return api.post(
    `/api/data-ingestion/tabledatafile/${id}/rows/discard/`,
    payload,
  );
};

export const editField = ({
  rowId,
  payload,
}: {
  rowId: string;
  payload: { columnPosition: number; rawValue: string; isEdited?: boolean };
}) => {
  return api
    .post(`/api/data-ingestion/rows/${rowId}/update-field/`, payload)
    .then((res) => res.data.data);
};

export const bulkEditField = ({
  dataFileId,
  payload,
}: {
  dataFileId: string;
  payload: {
    value: string;
    headerName: string;
    filters: { headerName: string; value: null | string }[];
  };
}) => {
  return api
    .post(
      `/api/data-ingestion/tabledatafile/${dataFileId}/rows/bulk-update-field/`,
      payload,
    )
    .then((res) => res.data.data);
};

export const bulkEditFieldV2 = ({
  dataFileId,
  payload,
}: {
  dataFileId: string;
  payload: {
    value: string;
    headerName: string;
    filters: { headerName: string; value: null | string }[];
  };
}) => {
  const serverPayload = {
    value: payload.value,
    headerName: mdmFieldCodec.encode(payload.headerName),
    filters: payload.filters.map((filter) => ({
      ...filter,
      headerName: mdmFieldCodec.encode(filter.headerName),
    })),
  };

  return api
    .post(
      `/api/data-ingestion/tabledatafile/${dataFileId}/rows/bulk-update-field/`,
      serverPayload,
    )
    .then((res) => res.data.data);
};

export const discardTemplate = (id: string) => {
  return api
    .post(`/api/data-ingestion/tabledatafile/${id}/discard-using-template/`)
    .then((res) => res.data);
};

export const discardFile = (id: string) => {
  return api
    .post(`/api/data-ingestion/tabledatafile/${id}/discard-file/`)
    .then((res) => res.data);
};

export const getConfiguredDataRows = (
  id: string,
  params: {
    pageNumber?: string | number;
    itemsPerPage?: string | number;
  } = {},
) => {
  return api
    .get<{
      data: TableDataFileValidatedRowsResponse;
    }>(`/api/data-ingestion/tabledatafile/${id}/data-configured-rows/`, {
      params: objectKeysToSnakeCase(params),
    })
    .then((res) => {
      const data = res.data.data;

      data.additional.headers = data.additional.headers.map((header) => ({
        ...header,
        headerName: mdmFieldCodec.decode(header.headerName),
        formatType: mdmFieldCodec.decode(header.formatType) as CellFormatType,
      }));
      return data;
    });
};

export const getConfiguredDataRowsV2 = (
  id: string,
  params: {
    pageNumber?: string | number;
    itemsPerPage?: string | number;
  } = {},
) => {
  return api
    .get<{
      data: TableDataFileValidatedRowsResponse;
    }>(`/api/data-ingestion/tabledatafile/${id}/data-configured-rows/v2/`, {
      params: objectKeysToSnakeCase(params),
    })
    .then((res) => {
      const data = res.data.data;

      data.additional.headers = data.additional.headers.map((header) => ({
        ...header,
        headerName: mdmFieldCodec.decode(header.headerName),
        formatType: mdmFieldCodec.decode(header.formatType) as CellFormatType,
      }));
      return data;
    });
};

export const getConfiguredRowsErrorsCount = (id: string) => {
  return api
    .get<TableDataFileTotalErrorsCountResponse>(
      `/api/data-ingestion/tabledatafile/${id}/data-configured-rows-count/`,
    )
    .then((res) => res.data.data.items[0].totalErrors);
};

export const setGranularityLevel = (
  dataFileId: string,
  payload: {
    granularityLevel: GranularityLevel;
  },
): Promise<void> => {
  return api.post(
    `/api/data-ingestion/tabledatafile/${dataFileId}/set-granularity-level/`,
    payload,
  );
};

export const getTemplateCollaborators = (
  templateId: string,
): Promise<TemplateCollaborator[]> => {
  return api
    .get(`/api/data-ingestion/templates/${templateId}/collaborators/`)
    .then((res) => {
      return res.data.data.items[0].collaborators;
    });
};

export const setTemplateCollaborators = (
  templateId: string,
  payload: TemplateCollaborator[],
): Promise<void> => {
  return api.post(
    `/api/data-ingestion/templates/${templateId}/collaborators/`,
    { collaborators: payload },
  );
};
